export const consolidator = {
  /**
   * Obtém dados da visão geral do consolidador.
   * @method POST
   * @param {Object} filters - Objeto contendo filtros a serem aplicados na consulta.
   * @return {Object} Resultado com a visão geral dos dados consolidados.
   */
  overview: '/v3/api/asset-allocation/consolidator/overview',

  /**
   * Obtém os filtros disponíveis para a visão geral.
   * @method GET
   * @return {Object} Lista de filtros que podem ser aplicados.
   */
  overviewFilters: '/v3/api/asset-allocation/consolidator/overview-filters',

  /**
   * Faz o download de um relatório com base nos parâmetros fornecidos.
   * @method POST
   * @param {Object} params - Objeto contendo os parâmetros necessários para a geração do relatório.
   * @return {String} Base64 - String em base64 que representa o relatório gerado para download.
   */
  downloadReport: '/v3/api/asset-allocation/consolidator/overview/export',

  /**
   * Faz o download do reltório ANBIMA com base nos parâmetros fornecidos.
   * @method POST
   * @param {Object} params - Objeto contendo os parâmetros necessários para a geração do relatório.
   * @return {String} Base64 - String em base64 que representa o relatório gerado para download.
   */
  downloadReportAnbima:
    '/v3/api/asset-allocation/relatorios-anbima/carteiras-administradas/export',

  /**
   * Obtém a lista de liquidez dos ativos.
   * @method GET
   * @param {Number} id_cliente - ID do cliente.
   * @return {Object} Dados da liquidez dos ativos.
   */
  assetsLiquidity: '/v3/api/asset-allocation/consolidator/assets-liquidity',
}

export const asset = {
  /**
   * @method PUT Editar eventos de um ativo
   */
  events: '/v3/api/asset-allocation/consolidator/events',

  /**
   * Deleta ativos em lote para um cliente.
   * @method DELETE
   * @param {Number} id_cliente_app - ID do cliente.
   * @param {Array} ids_ativos - Lista de ativos a serem deletados.
   */
  deleteInBatch: '/v2/api/professional/position/client/delete-in-batch',

  /**
   * Obtém eventos manuais de um ativo (retorna base64)
   * @method GET
   * @param {Number} asset_id - ID do ativo
   * @return {Object} { base64: string, name: string, extension: string }
   */
  getManualEvents: (asset_id) =>
    `/v2/api/professional/office/assets/${asset_id}/manual-events`,

  /**
   * Atualiza eventos manuais de um ativo (envia arquivo)
   * @method PUT
   * @param {Number} asset_id - ID do ativo
   * @param {File} file
   */
  updateManualEvents: (asset_id) =>
    `/v2/api/professional/office/assets/${asset_id}/manual-events`,
  /**
   * Busca PU histórico para um ativo.
   * @method POST
   */
  priceNearest: '/v2/api/assets/price/nearest',

  /**
   * @param {Number} id_cliente PUT. Path param. ID do cliente. OBRIGATÓRIO
   * @param {Number} id_ativo_cliente Path param. Campo 'id' do ativo do cliente. OBRIGATÓRIO
   * @param {Number} id_ativo_base Body. PK em base_ativos do escritório da sessão. OBRIGATÓRIO
   * @param {Boolean} overwrite_base_series Body. Substitui a série já existente na base. OPCIONAL
   * @param {Boolean} delete_client_series Body. Apaga a série do cliente. OPCIONAL
   * @returns {Object} Envelope com status, code, message e value. Codes em RELINK_BASE_ASSET_CODES.
   */
  relinkBaseAsset: ({ id_cliente, id_ativo_cliente }) =>
    `/v2/api/professional/position/client/${id_cliente}/assets/${id_ativo_cliente}/relink-base-asset`,
}

export const consolidateExtracts = {
  /**
   * @method POST
   * @param {FILE} file
   */
  import: '/v3/api/asset-allocation/cash-operations/import?save=true',
  /**
   * @method GET
   */
  export: `/v3/api/asset-allocation/cash-operations/export`,
  /**
   * @method GET
   */
  list: 'v3/api/asset-allocation/cash-operations/',
}

export const cripto = {
  /**
   * @method GET Buscar por criptoativos
   * @param {String} text Texto a ser buscado
   * @return {Array} [{nome, ratio}]
   */
  find: '/v2/api/general/search/criptoativos',
}

export const wallets = {
  /**
   * Trava carteiras/versões
   * @method POST
   * @param {Object} data - Dados da versão { nome_versao, descricao, comentario }
   * @param {String} data_fim - Data de fechamento no formato DD-MM-YYYY
   * @param {Array} client_ids - IDs dos clientes
   */
  lock: '/v3/api/asset-allocation/carteiras/versoes/travar-em-lote',
  /**
   * Trava uma carteira/versão específica
   * @method POST
   * @param {Number} client_id - ID da versão/carteira
   * @param {Object} data - Dados da versão { nome_versao, descricao, comentario }
   * @param {String} data_fim - Data de fechamento no formato DD-MM-YYYY
   */
  lockSingle: (client_id) =>
    `/v3/api/asset-allocation/carteiras/versoes/${client_id}`,
  /**
   * Destrava carteiras/versões
   * @method POST
   * @param {Array} client_ids - IDs dos clientes
   */
  unlock: '/v3/api/asset-allocation/carteiras/versoes/destravar-carteiras',
  /**
   * Alterar data de trava das carteiras/versões
   * @method POST
   * @param {String} data_fim - Nova data de fechamento no formato DD-MM-YYYY
   * @param {Array} client_ids - IDs dos clientes
   */
  changeLockDate:
    '/v3/api/asset-allocation/carteiras/versoes/alterar-trava-carteiras',
}

export const investmentAccounts = {
  /**
   * Integração com a API da corretora
   * @method POST
   * @param {Number} id_conta - ID da conta
   */
  brokerIntegration: (id_conta) =>
    `/v3/api/asset-allocation/integracoes/conta/${id_conta}/lambda`,
}

export const integrationLogs = {
  /**
   * Obtém logs de integração agrupados por dia para uma conta.
   * @method GET
   * @param {Number} id_conta - ID da conta de investimento
   * @return {Object} { id_conta, conta, logs_by_day: [...] }
   */
  logsByAccount: (id_conta) =>
    `/v2/api/professional/v3/api/aawz-bi/v2/api-integration-logs/account/${id_conta}`,

  /**
   * Obtém logs de integração para todas as contas de um cliente.
   * @method GET
   * @param {Number} id_cliente - ID do cliente
   * @return {Object} { id_cliente, accounts: [{ id_conta, conta, logs_by_day: [...] }] }
   */
  logsByClient: (id_cliente) =>
    `/v2/api/professional/v3/api/aawz-bi/v2/api-integration-logs/client/${id_cliente}`,

  /**
   * Faz o download de um arquivo de log (XLSX ou JSON) em base64.
   * @method GET
   * @param {String} log_id - UUID do log
   * @return {Object} { base64, name, extension }
   */
  downloadLogFile: (log_id) =>
    `/v2/api/professional/v3/api/aawz-bi/v2/api-integration-logs/${log_id}/download`,
}

export const backOfficeComparison = {
  /**
   * @method GET Dados de comparação api/extrato
   */
  getData: '/v3/api/asset-allocation/back-office/audit/api-comparison',
}

export const portfolioComparePdf = {
  /**
   * Inicia comparação de PDF de corretora com ativos AAWZ (async).
   * @method POST
   * @param {Number} idCliente - ID do cliente
   * @param {File} file - PDF da corretora
   * @param {Object} assets_statistics - Ativos AAWZ em JSON
   * @return {Object} { request_id } - ID da requisição assíncrona
   */
  compare: (idCliente) =>
    `/v2/api/professional/reports/portfolio/${idCliente}/compare-pdf`,
}

export const analysisRisk = {
  /**
   * @method GET
   */
  correlations: (idCliente) =>
    `/v3/api/asset-allocation/risk-analysis/${idCliente}/correlations`,
  /**
   * @method GET
   */
  metrics: (idCliente) =>
    `/v3/api/asset-allocation/risk-analysis/${idCliente}/metrics`,
  /**
   * @method GET
   */
  historicCorrelations: (idCliente) =>
    `/v3/api/asset-allocation/risk-analysis/${idCliente}/returns`,
  /**
   * @method GET
   */
  insights: (idCliente) =>
    `/v3/api/asset-allocation/risk-analysis/${idCliente}/insights`,
  /**
   * @method GET
   */
  analysisAdvanced: (idCliente) =>
    `/v3/api/asset-allocation/risk-analysis/${idCliente}/correlations/advanced`,
}

export const carteiraGrupoFamiliar = {
  /**
   * Consolidação da carteira de um GRUPO FAMILIAR.
   *
   * Devolve o mesmo payload da rota por cliente, calculado pelo backend sobre a
   * união dos ativos de todos os membros — nenhuma estatística é agregada no
   * frontend. O `id_escritorio` não vai na URL: vem da sessão (multi-tenancy).
   *
   * @method GET Consolidação. Query params iguais aos da rota por cliente, mais
   *   o filtro `id_cliente` (membro) e `group_by=id_cliente`.
   * @method OPTIONS Documentação viva + `group_data` com os valores disponíveis
   *   de cada filtro — é daqui que sai o filtro "Conta" da barra de filtros.
   * @param {String} id UUID do grupo familiar (`app_grupo_familiar.id`).
   */
  consolidation: ({ id }) =>
    `/v2/api/professional/portfolio/consolidation/family-group/${encodeURIComponent(
      id
    )}`,
}

